import { useCallback, useEffect, useMemo, useState } from "react";
import { Message } from "./Message";
import { servicesFor } from "../api/servicesFor";
import { repositoriesFor } from "../api/repositoriesFor";

// `userId` is the own id on the chat's server (`origin`, undefined = own server): the sender
// of own messages there.
export function usePolling(
  userId: string,
  ownerId: string | undefined,
  chatId: string | undefined,
  sharedKey?: JsonWebKey,
  origin?: string,
) {
  const [messages, setMessages] = useState<Message[]>();

  // Drop the previous chat's messages as soon as the chat identity changes.
  // Polling restarts with sinceId=undefined and would otherwise merge the new
  // chat's history onto the old one (message ids never collide across chats),
  // leaking one contact's plaintext messages into another's thread.
  useEffect(() => {
    setMessages(undefined);
  }, [ownerId, chatId, origin]);

  useEffect(() => {
    let mounted = true;

    const pollMessages = async () => {
      if (!sharedKey || !ownerId || !chatId) return;
      let sinceId: string | undefined = undefined;

      while (mounted) {
        try {
          const newMessages = await servicesFor(
            origin,
          ).messages.receiveDecryptedMessages(
            userId,
            ownerId,
            chatId,
            sinceId,
            sharedKey,
            sinceId === undefined ? 0 : 30, // wait=0 for initial load, wait=30 for long polling
          );

          if (newMessages.length > 0 && mounted) {
            setMessages((prev) => {
              const existingIds = new Set(prev?.map((p) => p.id) ?? []);
              const uniqueNew = newMessages.filter(
                (m) => !existingIds.has(m.id),
              );
              return [...(prev ?? []), ...uniqueNew];
            });
            sinceId = newMessages[newMessages.length - 1].id;
          } else {
            if (mounted) {
              setMessages((prev) => prev ?? []);
            }
            await new Promise((resolve) => setTimeout(resolve, 5000));
          }
        } catch (e) {
          console.error(e);
          if (mounted) {
            setMessages((prev) => prev ?? []);
          }
          await new Promise((resolve) => setTimeout(resolve, 5000));
        }
      }
    };

    if (sharedKey) {
      pollMessages();
    }

    return () => {
      mounted = false;
    };
  }, [userId, ownerId, chatId, sharedKey, origin]);

  // A sent message can already have arrived via the running long poll before
  // the POST response does - append it only if polling hasn't added it yet.
  const appendMessage = useCallback((message: Message) => {
    setMessages((prev) =>
      prev?.some((m) => m.id === message.id)
        ? prev
        : [...(prev ?? []), message],
    );
  }, []);

  return { messages, appendMessage };
}

// The newest message of the open chat as a ChatsList `liveActivity` (see
// Chats.tsx), so the list follows sent and polled messages.
export function useLiveActivity(
  chatId: string | undefined,
  messages: Message[] | undefined,
): { chatId: string; date: Date } | undefined {
  const timestamp = messages?.[messages.length - 1]?.timestamp;
  return useMemo(
    () =>
      chatId && timestamp ? { chatId, date: new Date(timestamp) } : undefined,
    [chatId, timestamp],
  );
}

// The time of a chat's newest message for the chat list (ADR 0021) - loaded
// once. undefined while loading, for a chat without messages and on failure
// (the list then simply shows no date).
export function useLastActivity(
  ownerId: string,
  chatId: string,
  origin?: string,
): Date | undefined {
  const [lastActivity, setLastActivity] = useState<Date>();
  useEffect(() => {
    let mounted = true;
    repositoriesFor(origin)
      .messages.fetchLastActivity(ownerId, chatId)
      .then((date) => mounted && setLastActivity(date))
      .catch((e) => console.warn("Failed to load the last activity", e));
    return () => {
      mounted = false;
    };
  }, [ownerId, chatId, origin]);
  return lastActivity;
}
